import { Box, Divider, VStack } from "@chakra-ui/react";
import { AccountAccessDetails } from "@syla/shared/types/responses/GetAccountsResponse";
import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { useFuseSearch } from "../../../hooks/useFuseSearch";
import { useHasVirtualKeyboard } from "../../../hooks/useHasVirtualKeyboard";
import { SelectOption } from "../../selector/parts/SelectOption";
import { SelectorList } from "../../selector/parts/SelectorList";
import { SelectorSearch } from "../../selector/parts/SelectorSearch";
import { useGridRovingTabIndex } from "../../selector/parts/useGridRovingTabIndex";
import { useSelectOptions } from "../../selector/parts/useSelectOptions";
import { SelectAccountOptionContent } from "./AccountOptionContent";

export type SelectAccountProps = {
  accounts: AccountAccessDetails[] | undefined;
  searchValue: string;
  onSearchTermChange: (searchTerm: string) => void;
  onSelect: (account: AccountAccessDetails) => void;
};

export function SelectAccount({
  accounts,
  searchValue,
  onSearchTermChange,
  onSelect,
}: SelectAccountProps) {
  const searchedAccounts = useFuseSearch(accounts, searchKeys, searchValue);

  const options = useMemo<SelectAccountOption[]>(
    () =>
      searchedAccounts.map((account) => ({
        value: account._id,
        data: {
          account,
        },
      })),
    [searchedAccounts],
  );

  const searchRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    ([value]: string[]) => {
      if (!value) {
        return;
      }

      const account = accounts?.find((account) => account._id === value);

      if (account) {
        onSelect(account);
      }
    },
    [accounts, onSelect],
  );

  const selector = useSelectOptions({
    allOptions: options,
    visibleOptions: options,
    selectedValues: EMPTY_SELECTED_VALUES,
    onChange: handleChange,
    enableMultiSelect: false,
    enableDeselect: false,
    moveSelectedToTop: false,
  });

  const focusSearchInput = useCallback(() => {
    searchRef.current?.focus();
  }, []);

  const {
    activeValue,
    focusIndex,
    containerRef,
    handleContainerKeyDown,
    handleContainerFocus,
  } = useGridRovingTabIndex({
    values: selector.sortedOptionValues,
    columnCount: 1,
    onActivate: selector.selectOption,
    onFinalArrowUp: focusSearchInput,
  });

  const handleSelectorSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          focusIndex(0);
        }
      },
      [focusIndex],
    );

  const hasVirtualKeyboard = useHasVirtualKeyboard();

  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    if (hasAutoFocusedRef.current) {
      return;
    }

    if (!hasVirtualKeyboard) {
      setTimeout(() => searchRef.current?.focus(), 0);
      hasAutoFocusedRef.current = true;
      return;
    }

    if (selector.sortedOptions.length > 0) {
      hasAutoFocusedRef.current = true;
      focusIndex(0);
    }
  }, [selector.sortedOptions.length, focusIndex, hasVirtualKeyboard]);

  return (
    <VStack
      w="full"
      flex={1}
      alignItems="flex-start"
      spacing={0}
      overflowX="hidden"
      minH={0}
    >
      <Box w="full" p={2}>
        <SelectorSearch
          searchValue={searchValue}
          onSearchChange={onSearchTermChange}
          searchRef={searchRef}
          onSearchKeyDown={handleSelectorSearchKeyDown}
        />
      </Box>

      <Divider borderColor="gray.300" borderBottomWidth="1px" />

      <SelectorList
        options={options}
        sortedOptions={selector.sortedOptions}
        selectedValueSet={selector.selectedValueSet}
        activeValue={activeValue}
        isLoading={!accounts}
        enableDoubleColumn={false}
        wrapText={false}
        containerRef={containerRef}
        onFocus={handleContainerFocus}
        onKeyDown={handleContainerKeyDown}
        onSelect={selector.selectOption}
        OptionContent={SelectAccountOptionContent}
      />
    </VStack>
  );
}

export type SelectAccountOptionData = {
  account: AccountAccessDetails;
};

export type SelectAccountOption = SelectOption<string, SelectAccountOptionData>;

const EMPTY_SELECTED_VALUES: string[] = [];

// do not inline, to ensure we have a stable reference
const searchKeys = ["nickname", "contactEmail", "fullName"];
